import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AccessEvent,
  AccessEventsDateRange,
  AccessEventsFilter,
  MobileOpCode,
  Query,
  QueryGetAccessEventsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useEffect, useRef, useState } from 'react';

dayjs.extend(utc);

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ACCESS_EVENTS_FOR_TENANT_TYPED: TypedDocumentNode<
  Pick<Query, 'getAccessEventsForTenant'>,
  QueryGetAccessEventsForTenantArgs
> = GET_ACCESS_EVENTS_FOR_TENANT;

interface Props extends BaseHookProps {
  fetchNextData: boolean;
  filter: null | AccessEventsFilter;
}

const getDateRange = (dateRange: AccessEventsDateRange) => ({
  rangeStart: dayjs(dateRange.rangeStart).second(0).millisecond(0).utc().format(),
  rangeEnd: dayjs(dateRange.rangeEnd).second(0).millisecond(0).subtract(1, 'millisecond').utc().format()
});

const useAccessEvents = ({ handleFetchError, fetchNextData, filter }: Props) => {
  const [accessEvent, setAccessEvent] = useState<AccessEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  // Bumped on every filter change so responses belonging to a previous filter can be ignored.
  const queryGenerationRef = useRef(0);
  // Mirrors `nextToken` but is cleared synchronously on filter change, so effects running in the same commit
  // never paginate the new filter with the previous filter's token.
  const nextTokenRef = useRef<string | null>(null);
  const [getAccessEvents, { loading }] = useLazyQuery(GET_ACCESS_EVENTS_FOR_TENANT_TYPED, {
    fetchPolicy: 'network-only'
  });

  const fetchAccessEvents = async (variables: QueryGetAccessEventsForTenantArgs) => {
    const generation = queryGenerationRef.current;
    try {
      const { data } = await getAccessEvents({ variables });
      if (generation !== queryGenerationRef.current) return;
      if (data) {
        const { items, nextToken: receivedNextToken } = data.getAccessEventsForTenant;
        setAccessEvent((current) => (variables.nextToken ? [...current, ...items] : items));
        nextTokenRef.current = receivedNextToken || null;
        setNextToken(nextTokenRef.current);
      }
    } catch {
      if (generation !== queryGenerationRef.current) return;
      handleFetchError(`Error while fetching Access Events`);
      nextTokenRef.current = null;
      setNextToken(null);
    }
  };

  useEffect(() => {
    queryGenerationRef.current += 1;
    nextTokenRef.current = null;
    setAccessEvent([]);
    setNextToken(null);
  }, [filter]);

  useEffect(() => {
    if (filter) {
      const { ...accessEventFiltersWithoutIsFilterApplied } = filter;
      void fetchAccessEvents({
        filter: {
          ...accessEventFiltersWithoutIsFilterApplied,
          actions: accessEventFiltersWithoutIsFilterApplied.actions
            ? [...accessEventFiltersWithoutIsFilterApplied.actions]
            : [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
          dateRange: filter.dateRange ? getDateRange(filter.dateRange) : undefined
        }
      });
    } else {
      void fetchAccessEvents({ filter: { actions: [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted] } });
    }
  }, [filter, getAccessEvents]);

  useEffect(() => {
    const currentNextToken = nextTokenRef.current;
    if (fetchNextData && currentNextToken) {
      if (filter) {
        const { ...accessEventFiltersWithoutIsFilterApplied } = filter;
        void fetchAccessEvents({
          nextToken: currentNextToken,
          filter: {
            ...accessEventFiltersWithoutIsFilterApplied,
            actions: accessEventFiltersWithoutIsFilterApplied.actions
              ? [...accessEventFiltersWithoutIsFilterApplied.actions]
              : [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
            dateRange: filter.dateRange ? getDateRange(filter.dateRange) : undefined
          }
        });
      } else {
        void fetchAccessEvents({
          nextToken: currentNextToken,
          filter: {
            actions: [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted]
          }
        });
      }
    }
  }, [fetchNextData, filter, getAccessEvents, nextToken]);

  return {
    accessEvents: accessEvent,
    isNextDataToDownload: nextToken !== null && !loading,
    loading
  };
};

export default useAccessEvents;
